// Thème basé sur les tokens Flutter + Design System Premium

export const ThemeTokens = {
  spacing: {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 24,
    xxl: 32,
    xxxl: 48,
  },
  radius: {
    sm: 6,
    md: 10,
    lg: 12,
    xl: 18,
    xxl: 22,
  },
  fontSize: {
    narrative: 17,
    narrativeMobile: 16,
    narrativeDesktop: 18,
    narrativeReading: 19.5, // Mode lecture : police confortable
    ui: 16,
    uiTitle: 28,
    stepTitle: 24,
    stepTitleReading: 28, // Titre plus imposant en mode lecture
    choiceText: 15,
    choiceTextReading: 17, // Choix plus lisibles en mode lecture
  },
  lineHeight: {
    narrative: 1.55,
    narrativeReading: 1.85, // Vraiment aéré en mode lecture (state of art)
    ui: 1.4,
    title: 1.2,
    choiceReading: 1.7, // Choix comme phrases de livre
  },
  // Design tokens pour élévation et ombres
  elevation: {
    card: {
      shadowOffset: {width: 0, height: 2},
      shadowRadius: 8,
    },
    cardHover: {
      shadowOffset: {width: 0, height: 4},
      shadowRadius: 12,
    },
    header: {
      shadowOffset: {width: 0, height: 1},
      shadowRadius: 4,
    },
    modal: {
      shadowOffset: {width: 0, height: 8},
      shadowRadius: 24,
    },
  },
  // Design tokens pour cartes premium
  cardPremium: {
    borderWidth: 1,
    borderRadius: 15,
    paddingVertical: 15,
    paddingHorizontal: 16,
    iconBadgeSize: 36,
    iconBadgeRadius: 10,
  },
  // Transitions pour animations douces
  transition: {
    fast: 150,
    normal: 250,
    slow: 350,
  },
};

export const LightTheme = {
  colors: {
    // Fond principal - Papier parchemin beige clair
    bg: '#E8DFD0', // Plus beige que précédemment
    
    // Surfaces - Nuances de beige/crème
    surface: '#F7F2EC', // Carte de texte principale
    surface2: '#F2EAE1', // Zones UI secondaires
    surface3: '#F5F3ED', // Alternative pour certains éléments
    
    // Cartes premium - Ivoire chaud avec relief
    cardBg: '#FAF7F3', // Base ivoire chaud
    cardBgTop: '#FDFBF8', // Highlight top pour gradient subtil
    cardBgBottom: '#F7F3EE', // Base bottom pour gradient
    cardBorder: '#D4C4B0', // Bordure chaleureuse visible
    cardShadow: '#8B7355', // Ombre douce dorée (pas noire)
    cardHighlight: 'rgba(255, 255, 255, 0.4)', // Inner highlight léger
    
    // Texte
    text: '#2C2520', // Texte principal sombre chaud
    textMuted: '#6B625A', // Texte secondaire
    textLight: '#8B7355', // Texte tertiaire
    
    // Accent - Couleur des éléments interactifs
    accent: '#8B7355', // Marron doré
    
    // Bordures et séparateurs
    border: '#D4C4B0', // Bordure principale
    borderLight: 'rgba(212, 196, 176, 0.4)', // Bordure discrète
    
    // Éléments interactifs - Tons beige/marron
    choiceBg: '#F0E6D8', // Boutons choix "kraft clair"
    choiceBorder: '#D4C4B0',
    choiceText: '#3A3429',
    choiceHover: '#E8D5C4',
    
    // Éléments spéciaux
    objectBg: 'rgba(232, 213, 196, 0.2)',
    objectBorder: 'rgba(139, 115, 85, 0.15)',
    
    // Avatar et cartes
    avatar: '#C8B8A8',
    avatarBorder: '#D4C4B0',
    
    // Ombres
    shadowOpacity: 0.12,
    shadowColor: '#000',
    
    // Toggle et contrôles
    toggleActive: '#8B7355',
    toggleInactive: '#D4C4B0',
  },
  ...ThemeTokens,
};

export const DarkTheme = {
  colors: {
    // Fond principal - Noir profond avec nuance marron (papier brûlé)
    bg: '#1A1614',
    
    // Surfaces - Tons marron foncé
    surface: '#2C2824', // Carte de texte principale
    surface2: '#353230', // Zones UI secondaires  
    surface3: '#3D3935', // Alternative pour certains éléments
    
    // Cartes premium - Papier brûlé avec relief
    cardBg: '#2E2A26', // Base papier brûlé
    cardBgTop: '#343029', // Highlight top pour gradient subtil
    cardBgBottom: '#2A2623', // Base bottom pour gradient
    cardBorder: '#4A4541', // Bordure chaleureuse
    cardShadow: '#000', // Ombre noire douce
    cardHighlight: 'rgba(255, 255, 255, 0.05)', // Inner highlight très subtil
    
    // Texte
    text: '#E8E6E1', // Texte principal clair
    textMuted: '#B8B0A8', // Texte secondaire
    textLight: '#9C8F7E', // Texte tertiaire
    
    // Accent - Couleur des éléments interactifs
    accent: '#A68968', // Marron doré plus clair pour le dark mode
    
    // Bordures et séparateurs
    border: '#4A4541', // Bordure principale
    borderLight: 'rgba(74, 69, 65, 0.4)', // Bordure discrète
    
    // Éléments interactifs - Tons marron moyen
    choiceBg: '#3D3935', // Boutons choix marron sombre
    choiceBorder: '#5A524A',
    choiceText: '#D4CFC4',
    choiceHover: '#4A4541',
    
    // Éléments spéciaux
    objectBg: 'rgba(61, 57, 53, 0.3)',
    objectBorder: 'rgba(90, 82, 74, 0.2)',
    
    // Avatar et cartes
    avatar: '#4A4541',
    avatarBorder: '#5A524A',
    
    // Ombres
    shadowOpacity: 0.35,
    shadowColor: '#000',
    
    // Toggle et contrôles
    toggleActive: '#8B7355',
    toggleInactive: '#5A524A',
  },
  ...ThemeTokens,
};

// MODE LECTURE - Version "papier livre" du thème clair
// Principes : moins de contraste, plus de chaleur, typo plus douce
export const ReadingLightTheme = {
  colors: {
    // Fond encore plus "papier ancien"
    bg: '#FBF8F2',
    
    // Surfaces plus douces, moins UI
    surface: '#F3ECE2',
    surface2: '#F8F3EA',
    surface3: '#F1EAE0',
    
    // Cartes premium - Papier livre ancien
    cardBg: '#F8F2E8',
    cardBgTop: '#FDFAF5',
    cardBgBottom: '#F5EFE5',
    cardBorder: '#E8DFD4',
    cardShadow: '#8B7355',
    cardHighlight: 'rgba(255, 255, 255, 0.3)',
    
    // Texte moins contrasté pour confort lecture longue
    text: '#2B2622',
    textMuted: '#5E564D',
    textLight: '#8B7A6E',
    
    // Accent - Couleur des éléments interactifs
    accent: '#8B5E34',
    
    // Bordures très discrètes
    border: '#E8DFD4',
    borderLight: 'rgba(232, 223, 212, 0.3)',
    
    // Choix plus "livre" que "bouton"
    choiceBg: '#F0E8DD',
    choiceBorder: '#E1D6C8',
    choiceText: '#362F28',
    choiceHover: '#E8DECD',
    
    // Objets encore plus subtils
    objectBg: 'rgba(232, 213, 196, 0.15)',
    objectBorder: 'rgba(139, 115, 85, 0.1)',
    
    // Avatar et cartes
    avatar: '#D4C8B8',
    avatarBorder: '#E1D6C8',
    
    // Ombres plus diffuses
    shadowOpacity: 0.08,
    shadowColor: '#8B5E34',
    
    // Toggle et contrôles discrets
    toggleActive: '#8B5E34',
    toggleInactive: '#D4C4B0',
  },
  ...ThemeTokens,
};

// MODE LECTURE - Version "papier livre" du thème sombre
export const ReadingDarkTheme = {
  colors: {
    // Fond très sombre mais pas noir pur
    bg: '#141210',
    
    // Surfaces douces, organiques
    surface: '#1D1A17',
    surface2: '#252220',
    surface3: '#2A2622',
    
    // Cartes premium - Papier brûlé lecture douce
    cardBg: '#1F1C19',
    cardBgTop: '#24211E',
    cardBgBottom: '#1A1714',
    cardBorder: '#3A3530',
    cardShadow: '#000',
    cardHighlight: 'rgba(255, 255, 255, 0.03)',
    
    // Texte moins agressif que mode normal
    text: '#E8E2DA',
    textMuted: '#B6AEA4',
    textLight: '#9C8F7E',
    
    // Accent - Couleur des éléments interactifs
    accent: '#B89B72',
    
    // Bordures subtiles
    border: '#3A3530',
    borderLight: 'rgba(58, 53, 48, 0.3)',
    
    // Choix mode livre
    choiceBg: '#252220',
    choiceBorder: '#3E3933',
    choiceText: '#D8D0C4',
    choiceHover: '#2A2622',
    
    // Objets très discrets
    objectBg: 'rgba(41, 38, 34, 0.25)',
    objectBorder: 'rgba(58, 53, 48, 0.15)',
    
    // Avatar et cartes
    avatar: '#3E3933',
    avatarBorder: '#4A4541',
    
    // Ombres diffuses
    shadowOpacity: 0.25,
    shadowColor: '#000',
    
    // Toggle et contrôles discrets
    toggleActive: '#B89B72',
    toggleInactive: '#4A4541',
  },
  ...ThemeTokens,
};

export type Theme = typeof LightTheme;
